import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Checkbox, Table } from "antd";
import ResizeObserver from "rc-resize-observer";
import { TableProps } from "antd/lib/table";
import { TableProps as RcTableProps } from "rc-table/lib/Table";
import { ColumnType } from "antd/es/table/interface";
import { VariableSizeGrid } from "react-window";
import { CustomizeScrollBody } from "rc-table/lib/interface";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import {
  Key,
  SorterResult,
  TablePaginationConfig,
} from "antd/lib/table/interface";
import { IObjectMap } from "../../../interfaces/common";
import Cell from "./Cell";
import { ROW_KEY } from "../../../constants/table";
import ResizableTitle from "../../tables/ResizableTitle";
import {
  DEFAULT_SORT_DIRECTION,
  DEFAULT_SORT_FIELD,
} from "../../../constants/sorting";
import eventEmitter from "../../../services/EventEmitter";

export interface VirtualTableProps<RecordType> extends TableProps<RecordType> {
  columns: ColumnType<RecordType>[];
  rowHeight?: number;
  scroll: RcTableProps<RecordType>["scroll"] & {
    scrollToFirstRowOnChange?: boolean;
  };
  loadMoreHandler: (parameters?: IObjectMap, forceUpdate?: boolean) => void;
  hasMoreItems: boolean;
  dataSource: RcTableProps<RecordType>["data"];
  selected?: Array<number | string>;
  changeSelectHandler?: (data: Array<number | string>) => void;
}

const defaultSorter = {
  field: DEFAULT_SORT_FIELD,
  order: DEFAULT_SORT_DIRECTION,
};

const VirtualTable = <T extends object>({
  selected,
  changeSelectHandler,
  hasMoreItems,
  loadMoreHandler,
  columns,
  scroll,
  rowHeight = 35,
  dataSource,
  rowClassName,
  ...props
}: VirtualTableProps<T>): React.ReactElement => {
  const [tableWidth, setTableWidth] = useState(0);

  const gridRef = useRef<VariableSizeGrid>(null);

  const onResize = useCallback(({ width }) => {
    setTableWidth(width);
  }, []);

  const onSelectAllHandler = useCallback(
    (event: CheckboxChangeEvent) => {
      if (!changeSelectHandler) {
        return;
      }

      if (event.target.checked) {
        // @ts-ignore
        changeSelectHandler(dataSource!.map((item) => item[ROW_KEY]));
        return;
      }

      changeSelectHandler([]);
    },
    [changeSelectHandler, dataSource]
  );

  const onSelectHandler = useCallback(
    (event: CheckboxChangeEvent, id: number | string) => {
      if (!changeSelectHandler) {
        return;
      }

      if (event.target.checked) {
        const data = (selected || []).filter((item) => item !== id);
        data.push(id);

        changeSelectHandler(data);
        return;
      }

      changeSelectHandler((selected || []).filter((item) => item !== id));
    },
    [changeSelectHandler, selected]
  );

  const mergedColumns = useMemo(() => {
    const cols = [...columns];
    if (selected) {
      cols.unshift({
        title: () => (
          <Checkbox
            indeterminate={
              selected.length > 0 && dataSource!.length > selected.length
            }
            onChange={onSelectAllHandler}
          />
        ),
        dataIndex: "id",
        key: "id",
        sorter: false,
        width: 32,
        render: (text, item) => {
          // @ts-ignore
          const identification = item[ROW_KEY] as number | string;

          return (
            <Checkbox
              defaultChecked={selected.includes(identification)}
              onChange={(event) => onSelectHandler(event, identification)}
            />
          );
        },
      });
    }

    const widthColumnCount = cols!.filter(({ width }) => !width).length;

    return cols.map((column) => {
      if (column.width) {
        return column;
      }

      return {
        ...column,
        width: Math.floor(tableWidth / widthColumnCount),
      };
    });
  }, [
    columns,
    dataSource,
    onSelectAllHandler,
    onSelectHandler,
    selected,
    tableWidth,
  ]);

  const gridHeight = useMemo(() => {
    return dataSource!.length * rowHeight;
  }, [dataSource, rowHeight]);

  const onTopScroll = useCallback(
    (val) => {
      if (!hasMoreItems) {
        return;
      }

      if (val >= gridHeight - (scroll!.y as number)) {
        loadMoreHandler();
      }
    },
    [hasMoreItems, loadMoreHandler, scroll, gridHeight]
  );

  useEffect(() => {
    eventEmitter.on("table:re-render", () => {
      gridRef.current?.resetAfterIndices({
        rowIndex: 0,
        columnIndex: 0,
        shouldForceUpdate: true,
      });
    });

    return () => {
      eventEmitter.off("table:re-render", () => {});
    };
  }, []);

  const renderVirtualList: CustomizeScrollBody<T> = (
    rawData: Array<T>,
    { onScroll, scrollbarSize }
  ) => (
    <VariableSizeGrid
      ref={gridRef}
      className="virtual-grid"
      columnCount={mergedColumns.length}
      columnWidth={(index: number) => {
        const { width } = mergedColumns[index];
        return gridHeight > scroll!.y! && index === mergedColumns.length - 1
          ? (width as number) - scrollbarSize - 1
          : (width as number);
      }}
      height={scroll?.y as number}
      rowCount={rawData.length}
      rowHeight={() => rowHeight}
      width={tableWidth}
      onScroll={({ scrollLeft, scrollTop }) => {
        onScroll({ scrollLeft });
        onTopScroll(scrollTop);
      }}
    >
      {({
        columnIndex,
        rowIndex,
        style,
      }: {
        columnIndex: number;
        rowIndex: number;
        style: React.CSSProperties;
      }) => (
        <Cell
          column={mergedColumns[columnIndex] as ColumnType<T>}
          value={
            (rawData[rowIndex] as Array<object>)[
              mergedColumns[columnIndex].dataIndex as number
            ]
          }
          className={rowClassName as (value: Object) => string}
          index={rowIndex}
          entry={rawData[rowIndex]}
          isLast={columnIndex === mergedColumns.length - 1}
          isSelectedCell={selected && columnIndex === 0}
          style={style}
        />
      )}
    </VariableSizeGrid>
  );

  const onTableChangeHandler = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, (Key | boolean)[] | null>,
      sorter: SorterResult<T> | SorterResult<T>[]
    ): void => {
      loadMoreHandler(
        {
          page: pagination.current,
          sortColumn: Array.isArray(sorter)
            ? defaultSorter.field
            : sorter.field,
          sortDirection: Array.isArray(sorter)
            ? defaultSorter.order
            : sorter.order,
          grouping: filters,
        },
        true
      );
    },
    [loadMoreHandler]
  );

  return (
    <ResizeObserver onResize={onResize}>
      <Table
        {...props}
        scroll={{ y: scroll.y }}
        className="virtual-table"
        columns={mergedColumns}
        pagination={false}
        dataSource={dataSource}
        rowKey={ROW_KEY}
        onChange={onTableChangeHandler}
        components={{
          header: {
            cell: ResizableTitle,
          },
          body: dataSource!.length > 0 ? renderVirtualList : undefined,
        }}
      />
    </ResizeObserver>
  );
};

export default VirtualTable;
